/**
 * NOS-241: SEO-continuity soft redirects. When a public-site path 404s, look
 * it up in org_redirects (written at import: source path → new path) and give
 * the caller a target to navigate to. SPA-interim behavior — M22 (Astro SSR)
 * serves real HTTP 301s from the same table.
 */
import { useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"

/** Site-relative path normalized the way org_redirects stores source paths. */
export function normalizeRedirectPath(path: string): string {
  const p = path.split(/[?#]/)[0].replace(/\/+$/, "")
  return p === "" ? "/" : p
}

export type ImportRedirectState =
  | { status: "checking" }
  | { status: "none" }
  | { status: "found"; target: string }

/**
 * Resolve `sitePath` against the org's redirect map. Only runs when `enabled`
 * (i.e. the page actually 404'd) — a hit on the exact path wins, then the
 * lowercase variant (WP URLs are canonically lowercase).
 */
export function useImportRedirect(
  orgId: string | null | undefined,
  sitePath: string,
  enabled: boolean
): ImportRedirectState {
  const [state, setState] = useState<ImportRedirectState>(
    enabled ? { status: "checking" } : { status: "none" }
  )

  useEffect(() => {
    if (!enabled || !orgId) {
      setState({ status: "none" })
      return
    }
    let cancelled = false
    setState({ status: "checking" })
    const path = normalizeRedirectPath(sitePath)
    const candidates = path === path.toLowerCase() ? [path] : [path, path.toLowerCase()]
    supabase
      .from("org_redirects")
      .select("source_path, target_path")
      .eq("org_id", orgId)
      .in("source_path", candidates)
      .limit(candidates.length)
      .then(({ data }) => {
        if (cancelled) return
        // Exact match wins over the lowercase fallback
        const exact = data?.find((r) => r.source_path === path) ?? data?.[0]
        if (exact?.target_path) {
          setState({ status: "found", target: exact.target_path })
        } else {
          setState({ status: "none" })
        }
      })
    return () => {
      cancelled = true
    }
  }, [orgId, sitePath, enabled])

  return state
}
